import { use, useMemo } from 'react';

import { useLocale } from '@/core/hooks/use-locale';
import type { Dictionary } from '@/core/providers/dictionary-provider';
import { getDictionary } from '@/core/utils/dictionary';

// Cache for loaded dictionaries to avoid re-fetching
const dictionaryCache = new Map<string, Promise<Dictionary>>();

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    dictionaryCache.clear();
  });
}

/**
 * Hook that loads the dictionary for the current locale using React's use() hook.
 * Works with Suspense boundaries for better async data loading.
 *
 * @returns The loaded dictionary
 */
export function useDictionaryLoader(): Dictionary {
  const locale = useLocale();

  // Get or create dictionary promise with caching
  const dictionaryPromise = useMemo(() => {
    if (dictionaryCache.has(locale)) {
      return dictionaryCache.get(locale)!;
    }

    const promise = getDictionary(locale);
    dictionaryCache.set(locale, promise);
    return promise;
  }, [locale]);

  // React.use() unwraps the promise and works with Suspense
  return use(dictionaryPromise);
}
